/**
 * ConfirmModal — a simple confirmation dialog rendered as a portal overlay.
 *
 * Props:
 *   open        {boolean}  — whether the modal is visible
 *   title       {string}   — heading text
 *   description {string}   — body text explaining what the user is confirming
 *   confirmLabel {string}  — label for the confirm button (default: "Confirm")
 *   cancelLabel  {string}  — label for the cancel button (default: "Cancel")
 *   destructive  {boolean} — if true, the confirm button uses the destructive variant
 *   onConfirm   {Function} — called when the user clicks confirm
 *   onCancel    {Function} — called when the user clicks cancel or the backdrop
 */
import { Button } from './button';

export function ConfirmModal({
    open,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    onConfirm,
    onCancel,
}) {
    if (!open) return null;

    return (
        // Backdrop — clicking outside cancels
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={onCancel}
        >
            {/* Dialog panel — stop click propagation so clicking inside doesn't cancel */}
            <div
                className="bg-card border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 space-y-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div>
                    <h2 className="text-lg font-semibold">{title}</h2>
                    {description && (
                        <p className="text-sm text-muted-foreground mt-1">{description}</p>
                    )}
                </div>

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onCancel}>
                        {cancelLabel}
                    </Button>
                    <Button
                        variant={destructive ? 'destructive' : 'default'}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
}
